/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AbstractFormComponent, BuilderNavigationService } from '@gh/builder-shared';
import { AelEvents, BuilderPermission, SalespersonBudgetType } from '@gh/builder-shared-data';
import {
  ActionFlow,
  ContainerState,
  EnumDictionary,
  getRouteInitData,
  ImmutablePageState,
  queryDirty,
  queryFormEditable,
  queryFormMode,
  State,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { MONTH_LOGICAL_NAMES } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';

import { assign, fromPairs } from 'lodash';
import { Observable } from 'rxjs';

import {
  calculateSbFooterItem,
  getSbListItemId,
  SB_YEARS,
  SbListItem,
  SbListItemType,
} from '../../resources/sb-list.model';
import { SbGridDefinition } from './sb-grid-definition.service';
import { SbListCommandService, SbSaveFlowState } from './sb-list-command.service';
import { sbListReducer } from './sb-list.reducers';
import {
  querySbCurrentItems,
  querySbFilter,
  querySbSelectedFocusedCell,
  querySbSelectedItemId,
  SB_LIST_INIT_STATE,
  SbFilter,
  SbListState,
} from './sb-list.state';

export function sbListStateFactory(): State<SbListState> {
  return new State(SB_LIST_INIT_STATE, sbListReducer);
}

@Component({
  standalone: false,
  selector: 'gh-salesperson-budget-list',
  templateUrl: 'salesperson-budget-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: State, useFactory: sbListStateFactory },
    SbListCommandService,
    SbGridDefinition,
  ],
})
export class SalespersonBudgetListComponent extends AbstractFormComponent<SbListState> implements OnInit {
  years = SB_YEARS;
  types: EnumDictionary<SalespersonBudgetType>;
  filter$: Observable<SbFilter>;
  items$: Observable<SbListItem[]>;
  footer$: Observable<SbListItem[]>;
  selectedItemId$: Observable<Maybe<ID>>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  form: ContainerState<SbListState>;

  sbFooterTitleItem = <SbListItem>{
    type: SbListItemType.FooterTitle,
    rowId: 'footer-title',
    ...fromPairs(MONTH_LOGICAL_NAMES.map((month) =>
      [month, this.translationService.get(`lbl_${month}_middle`)])),
  };

  constructor(private route: ActivatedRoute,
              private state: State<SbListState>,
              private commands: SbListCommandService,
              private userContext: UserContext,
              public gridDefinition: SbGridDefinition,
              private navigationService: BuilderNavigationService,
              private translationService: TranslationService) {
    super();

    this.form = new ImmutablePageState(state, {
      dirty: queryDirty,
      formMode: queryFormMode,
    });
  }

  get editable(): boolean {
    return this.state.project(queryFormEditable);
  }

  get completeFlow(): ActionFlow<SbSaveFlowState> {
    return this.commands.completeFlow();
  }

  ngOnInit(): void {
    const { items, ...init } = getRouteInitData(this.route);

    assign(this, init);

    const mode = this.userContext.hasPermission(BuilderPermission.Sales.Budgets.EDIT) ?
      FormMode.EditMode : FormMode.ViewMode;
    this.form.build(items);
    this.commands.load(items, mode);

    this.filter$ = this.state.select(querySbFilter);
    this.items$ = this.state.select(querySbCurrentItems);
    this.footer$ = this.state.select(querySbCurrentItems).pipe(
      map$((currentItems) => [this.sbFooterTitleItem, calculateSbFooterItem(currentItems)]));
    this.selectedItemId$ = this.state.select(querySbSelectedItemId);
    this.focusedCell$ = this.state.select(querySbSelectedFocusedCell);
  }

  setFilterProperty(name: string, value: any): void {
    this.commands.setFilterProperty(name, value);
  }

  onAdd(): void {
    this.commands.addItems();
  }

  onRemove(item: SbListItem): void {
    this.commands.removeItem(getSbListItemId(item));
  }

  onSelectedItemChanged(): void {
    this.commands.resetSelectedItem();
  }

  onFocusedCellChange(): void {
    this.commands.resetFocusedCell();
  }

  onReset(): void {
    this.commands.reset();
  }

  goToAel(): void {
    this.navigationService.goToAel({
      label: 'lbl_budgets',
      entityType: 'OFFICE',
      eventTypeIds: [AelEvents.Office.EditedSalespersonBudgets],
    });
  }

  canBeDeleted(item: SbListItem): boolean {
    return item.type === SbListItemType.Item;
  }
}
